/**
 * Phase 6a — typed event bus for the AHU Board (the React home of the legacy
 * Backbone `dispatcher`, controller/dispatcher.js).
 *
 * THE DISPATCHER CONTRACT (PHASE6_AHUBOARD_INVENTORY.md §25 — authoritative
 * event map; payloads are positional args exactly like Backbone triggers):
 *
 * §25.1 Creation / selection backbone
 *  - create:component:any   [{tunnel_index_id, component_name, component_value,
 *                             order?, isCtrlPressed?}]      pubs: toolbar/insert popup
 *                             → AUTO-WIRE api.component.create (6e)
 *  - create:accessory:any   (name)                          → AUTO-WIRE api.accessory.create (6e)
 *  - duplicate:accessory:any(section_id, type, modelJSON, positionJSON|null,
 *                            sameSection, tunnel_id)        → AUTO-WIRE api.accessory.duplicate (6e)
 *  - select:section         ({section, tab?, isCtrlPressed?, from_storage?,
 *                             selected_sections?})          → AUTO-WIRE api.section.select
 *                             (React: ahuBoardSlice.selectSection + DOM-sync rows of §13)
 *  - update:multiple:sections:list ()                       → sections tab multi list (6e)
 *  - select:accessory       (id|null)                       → MainView #accessories-ul arm (slice)
 *  - select:deselect:accessory (id, isChecked)              → canvas recolor (6c)
 *  - hover:accessory        (id, bool)                      → canvas (6c — same handler)
 *  - hover:section          ({section|null, color?, accessory?}) → canvas tint (6c)
 *  - force:select:section   (id, error, detect_error, color, ctrl?) → canvas (6c)
 *  - deselect:section       (same shape)                    → canvas (6c)
 *  - remove:selected:section ()                             → viewdata reset (slice.clearSelection)
 *  - added:new:component / add:new:component:preloader / add:new:component:form /
 *    component:created / added:new:accessory / auto:select:component /
 *    storage:section.model:create / change:current:tab      → panel-domain (6e)
 *
 * §25.2 Geometry / canvas redraw (ALL consumed by the canvas — attach 6b/6c)
 *  - show:on:canvas, change:tunnel:dimensions, sections:reordered,
 *    validate:tunnel:after:section:destroy, redraw:canvas,
 *    draw:accessory:on:canvas, remove_accessory_from_canvas,
 *    draw:int_wall:on:canvas, update:int_wall:on:canvas,
 *    remove:int_wall:from:canvas, draw:coil:connection,
 *    draw:evap_pack:connection, redraw:tunnel:lugs, redraw:tunnel:lug,
 *    change:active:lugs, change:casing:section_<id> (dynamic),
 *    canvas:zoom, canvas:rotate, canvas:set:view, canvas:change:tunnel:index,
 *    change:section:tag:canvas, change_canvas_color_mode
 *  - [NEG]#89: update_dmount_dimension is a DEAD edge (no subscriber) — NOT in
 *    this bus. The real dmount-dim refresh is the dmounts-collection listeners
 *    (Part 1 §3.1 step 6) → 6c.
 *
 * §25.3 Validation / error-manager (6e)
 *  - accessoriesWithError, sectionsWithError, validate:all:tunnels,
 *    validate:tunnel:components:only, complete:errors:resolving,
 *    get:overall:height, damper:fan_intake:crossing,
 *    changed:section:<id>:eadb_temp:eawb_temp (dyn),
 *    changed:secrion_<id>:casing_insulation_id (dyn — TYPO IS LOAD-BEARING,
 *    §26.3 rule 3), rerate:fan:data, check:fans:need_to_rate
 *  - [NEG]#71: validate:section:accessories:with_plane — dead, excluded.
 *
 * §25.4 Tunnel/section/lug bookkeeping (panel domain, 6e)
 *  - section:length:changed, changed:tunnel_<id>:sizes, changed:tunnel_<id>:width,
 *    update:tunnel:<floorId>:fields, tunnelLugs:recalcPlacement,
 *    recalc:lug:placement, render_tunnel_lugs, sections_collection:created/removed,
 *    tunnel:removed, section:destroy:<id>, dmount:destroy:section:<id>,
 *    minRequiredLengthChange, section:render:tag, section:rendered,
 *    change:section:tag:<id>, sections:bulk_update, internal_wall:created/
 *    deleted/change, model:vertical:int_wall:saved (BOTH planes fire the
 *    vertical name — copy verbatim, §19.2), hood:removed:from:section,
 *    remove_accessory_esp_<id>, remove_component_esp_<id>,
 *    remove_damper_accessory_<sectionId>, refetch_fan_<id>_isp,
 *    force:render:fan:<id>, rotate:smart:list:<id>, ahu2d:section:<id>:accessories (Q12)
 *
 * §25.5 Chrome / mode events
 *  - storage:viewdata:change → THE selection fan-out; in React this is the
 *    ahuBoardSlice subscription. The channel exists here ONLY for legacy
 *    Backbone islands during the 6d transition (§13 transition rule).
 *  - storage:<classID>:{change,destroy,sync,add,remove} families (§19.1),
 *    rating_mode:activate/hide, rotate:canvas, rotate:canvas:<kind>:table ×23,
 *    choose:<x>:master, close:manual-mode-active, open_rating_popup,
 *    show:duplicate:popup, show:delete:popup, certifications:save,
 *    update:ahu:data, update:ahus, workflow:done, hide:show_allowable_filter_sizes
 *
 * DEAD events excluded per [NEG]: force:render:sections (#37), hide:form:active
 * (#39), canvas:rendered (2D era), check:status, remove_selected_sections (#70),
 * update_dmount_dimension (#89), validate:section:accessories:with_plane (#71).
 *
 * 6a STATUS: the bus is live with NO-OP consumers registered for the canvas
 * channels (registerNoopCanvasConsumers below — replaced by the real canvas in
 * 6b/6c). Draw-replay channels are BUFFERED: events emitted before a real
 * consumer subscribes are queued and replayed on first subscribe — this
 * replaces the storage.js:132 300 ms `$('#canvas_section canvas')` polling
 * loop for int-wall draws (parity 82/218).
 */

import type { AhuViewPreset } from 'app/slices/ahuBoardSlice';

// ---------------------------------------------------------------------------
// Static event payload map (positional args, mirroring Backbone trigger args).
// Payloads typed `unknown` where the producing surface lands in a later
// sub-phase (the §25 row above is the contract; types tighten as ports land).
// ---------------------------------------------------------------------------
export interface AhuBusEvents {
  // ---- §25.1 creation / selection ----
  'create:component:any': [blocks: Array<Record<string, unknown>>];
  'create:accessory:any': [name: string];
  'duplicate:accessory:any': [
    sectionId: number,
    type: string,
    model: Record<string, unknown>,
    position: Record<string, unknown> | null,
    sameSection: boolean,
    tunnelId: number,
  ];
  'select:section': [payload: {
    section: unknown;
    tab?: 'ahu' | 'sections' | 'components' | 'accessories' | '';
    isCtrlPressed?: boolean;
    from_storage?: unknown[];
    selected_sections?: unknown[];
  }];
  'update:multiple:sections:list': [];
  'select:accessory': [accessoryId: number | null];
  'select:deselect:accessory': [accessoryId: number, isChecked: boolean];
  'hover:accessory': [accessoryId: number, hover: boolean];
  'hover:section': [payload: { section: unknown; color?: number | string; accessory?: unknown }];
  'force:select:section': [
    sectionId: number,
    error?: boolean,
    detectError?: boolean,
    color?: number | string,
    isCtrlPressed?: boolean,
  ];
  'deselect:section': [
    sectionId: number,
    error?: boolean,
    detectError?: boolean,
    color?: number | string,
    isCtrlPressed?: boolean,
  ];
  'remove:selected:section': [];
  'added:new:component': [section?: unknown];
  'add:new:component:preloader': [payload: { section: unknown; is_new: boolean }];
  'add:new:component:form': [component: unknown, type: string];
  'component:created': [components: unknown, value: string];
  'added:new:accessory': [accessoryId: number, name: string];
  'auto:select:component': [sectionId: number, tunnelId?: number];
  'storage:section.model:create': [payload: { section: unknown; sections: unknown }];
  'change:current:tab': [arg?: number];

  // ---- §25.2 geometry / canvas redraw (canvas consumers attach 6b/6c) ----
  'show:on:canvas': [component: unknown, isCtrl?: boolean];
  'change:tunnel:dimensions': [payload: {
    tunnel_id: number;
    model?: unknown;
    render?: boolean;
    check?: boolean;
    only_for?: number;
    accessories_fetch?: boolean;
  }];
  'sections:reordered': [tunnelId: number];
  'validate:tunnel:after:section:destroy': [payload: {
    sectionId: number;
    tunnelId?: number;
    skip?: boolean;
    tunnel_removed?: boolean;
  }];
  'redraw:canvas': [];
  'draw:accessory:on:canvas': [
    accessory: Record<string, unknown>,
    label: string,
    section: unknown,
    mounting: Record<string, unknown>,
    isAlreadyRendered: boolean,
    viewport?: Record<string, unknown> | null,
    intWall?: Record<string, unknown> | null,
  ];
  'remove_accessory_from_canvas': [accessoryId: number];
  /**
   * The accessory row was DELETED from the database. Deliberately distinct from
   * `remove_accessory_from_canvas`, which is a purely VISUAL hide: the accessory
   * draw gate's remove paths (accessoryDrawGate — plane 9, a blade direction
   * invalid for the new plane, a 4-sided hood with no `height_2`, an unchecked
   * fan VFD, a vanished internal wall) take a live, still-existing row off the
   * canvas and say nothing about whether it still exists. Legacy has exactly one
   * listener on that channel — `canvas/tunnel.js:172-178`, the canvas itself —
   * so anything that must react to a REAL deletion listens here instead.
   */
  'accessory:deleted': [accessoryId: number];
  'draw:int_wall:on:canvas': [wallId: number, wall: Record<string, unknown>, redraw?: boolean];
  'update:int_wall:on:canvas': [wall: Record<string, unknown>];
  'remove:int_wall:from:canvas': [wall: Record<string, unknown>];
  'draw:coil:connection': [
    coil: Record<string, unknown>,
    sectionId: number,
    qtyStacked: number,
    dutyId: number,
    hFromFloor: number | string | null,
    mountingAngle: number | string | null,
  ];
  'draw:evap_pack:connection': [
    data: { fill?: unknown; drain?: unknown; overflow?: unknown },
    sectionId: number,
  ];
  'redraw:tunnel:lugs': [tunnelId: number, removed?: boolean];
  'redraw:tunnel:lug': [tunnelId: number, lug: Record<string, unknown>, deleted?: boolean];
  'change:active:lugs': [activeLugs?: Array<Record<string, unknown>>];
  /** Pane → canvas: select these drawn lugs as if they had been clicked. */
  'select:tunnel:lugs': [lugIds: Array<number | string>];
  /**
   * Data Bar → canvas: tint these tunnels' base rails; `[]` clears. No legacy
   * counterpart — the Backbone branch serves `same_base_tunnel_ids` and never
   * reads it client-side. The Base select emits the edited tunnel plus its
   * `same_base_tunnel_ids` peers after a save, which is exactly the set
   * `_resync_hybrid_base_ids` just rewrote server-side.
   */
  'highlight:tunnel:bases': [tunnelIds: Array<number | string>];
  'canvas:zoom': [direction: 'zoom-in' | 'zoom-out'];
  'canvas:rotate': [autoRotate: boolean];
  'canvas:set:view': [view: AhuViewPreset, animate?: boolean];
  'canvas:change:tunnel:index': [tunnelId: number | 'all', prevTunnelId: number | 'all' | undefined];
  'change:section:tag:canvas': [sectionId: number, tag: string];
  'change_canvas_color_mode': [showColor: boolean];

  // ---- §25.3 validation / error-manager (6e) ----
  'accessoriesWithError': [byAccessoryId: Record<number, string>];
  'sectionsWithError': [
    bySectionId: Record<number, string>,
    messagesBySectionId: Record<number, string[]>,
  ];
  'validate:all:tunnels': [floorId?: number];
  'validate:tunnel:components:only': [payload: { tunnelId?: number; sectionId?: number }];
  'complete:errors:resolving': [tunnelIds: number[], error?: unknown, prevCount?: number];
  'get:overall:height': [force?: boolean];
  'damper:fan_intake:crossing': [sectionId: number, damperId: number];
  'rerate:fan:data': [payload: Record<string, unknown>];
  'check:fans:need_to_rate': [delay?: number];
  /** §36 — fan combinations popup → fan selector: bust TCF cache, re-rate. */
  'params_fan:change_combinations': [];

  // ---- §25.4 bookkeeping (6e) ----
  /** Cabinet pre-save W/H notice (§14 — listeners: evap form 6e2). */
  'change:tunnel:widthOrHeight': [payload?: { tunnel_id?: number; name?: string; value?: unknown }];
  /** Cabinet/section outer-skin change ({elem} in legacy; §7.4 echo). */
  'change:tunnel:casing': [payload?: { tunnel_id?: number; elem?: unknown }];
  'section:length:changed': [];
  'tunnelLugs:recalcPlacement': [payload: { tunnel_id: number; hide_from_canvas?: boolean }];
  /**
   * Actions → pane: this tunnel's placement has just been re-read from the
   * server, here is the body. Legacy needed no such channel — the pane and the
   * canvas shared ONE `LugCollection`, so a refetch from anywhere reached both.
   * React split them (the canvas reads the query cache; the pane keeps its own
   * `placements` mirror so it can apply per-lug edits optimistically), and an
   * AHU-wide refetch driven from outside the pane only ever reached the canvas
   * half. This carries it the rest of the way.
   */
  'tunnelLugs:placementRefetched': [tunnelId: number, placement: Record<string, unknown>];
  'recalc:lug:placement': [];
  'render_tunnel_lugs': [];
  'sections_collection:created': [tunnelId?: number];
  'sections_collection:removed': [tunnelId?: number];
  'tunnel:removed': [tunnelId?: number];
  'minRequiredLengthChange': [];
  'section:render:tag': [];
  'section:rendered': [sectionIds: number[]];
  'sections:bulk_update': [sectionIds: number[], name: string, value: unknown];
  'internal_wall:created': [];
  'internal_wall:deleted': [];
  'internal_wall:change': [];
  'model:vertical:int_wall:saved': [];
  'hood:removed:from:section': [];
  /** A louver changed in a way the damper mounted on it has to be re-read for:
   *  deleted, reselected, moved, re-planed, or turned. The server has either
   *  re-sized, re-planed and re-centred that damper or handed it back to the
   *  louver's wall; either way the damper's form is stale, and while it still
   *  believes it is mounted its airway size, casing mount, fan VP flag and x/y
   *  rows stay locked.
   *
   *  The louver's SECTION rides along because the change also moves the louver
   *  in and out of `AHUAccessoryDamper.mountable_louvers` — a louver only
   *  becomes mountable once it has a catalogue selection and a position — so
   *  the other dampers in that section have a stale mounting list too.
   */
  'louver:damper:recheck': [louverId: number, sectionId?: number];

  // ---- Part 6 (forms layer) static events — §53.3 rule 5 ----
  /** §49.3 fan ESP suite: ({id, air_stream_ids}) → 1 s debounce FanEsp fetch. */
  'fetch:fans:esp': [payload: { id?: number; air_stream_ids?: number[] }];
  /** §51.2 hood↔damper web (static halves; per-id variants are dynamic).
   * 6e2b-2: payloads live — (modelJSON, hood_is_mounting?, model_is_hood?);
   * the model JSON carries id/section_id/max_width/max_height exactly like
   * the legacy Backbone-model sender (both ends are React in this cut). */
  'update:damper:params': [
    model: Record<string, unknown>,
    hoodIsMounting?: boolean,
    modelIsHood?: boolean,
  ];
  'update:damper:count': [model: Record<string, unknown>];
  'update:damper:fields': [model: Record<string, unknown>];
  /** §49.6 drain-pan refresh (900 ms fetch+render). */
  'changeDrainPan': [];
  /** §49.4 with_rating embedded smart-list release (Part 4 §30.6). */
  'smart_list_rotate_back': [];
  /** §49.6 changeTagOnBoard (parity 339) — row-label + toolbar tag rewrite. */
  'update:accessories:tag:onboard': [payload: { tag: string; id: number }];
  /** §51.2 hood→damper mounting-list re-render (render_damper_mounting,
   * accessory_view.js:1787-1791) — 6e2b-2 consumer; typed now per §53.3 #5. */
  'render:damper:mounting': [
    position: Record<string, unknown> | undefined,
    damperValue: unknown,
    damperName: unknown,
  ];

  // ---- §25.5 chrome / mode ----
  'storage:viewdata:change': [model: unknown, changed: unknown];
  /**
   * §30.2 chassis activation contract. `name` is the selector tab; the BOARD
   * host fills the rest from the store (the TabAhu / FormsPanel emitters send
   * only {name} or {name,loading}). `loading:true` → preloader-only one-shot.
   * The optional context lets a sender pass models directly when it has them
   * (selection_mode.js parity); special_options sources ahu from the store. */
  'rating_mode:activate': [payload: {
    name: string;
    loading?: boolean;
    ahu?: Record<string, unknown> | null;
    project?: Record<string, unknown> | null;
    section?: Record<string, unknown> | null;
    /** Dual-tunnel partner (`sections.find(s => s.sitting_on_id === section.id)`).
     * Legacy has no such field — its selectors walk `current_model.view.sections`
     * and so see EVERY section the component spans (energy_wheel.selector.js:489). */
    section2?: Record<string, unknown> | null;
    current_model?: Record<string, unknown> | null;
    rated_coil_model?: Record<string, unknown> | null;
    rated_phx_model?: Record<string, unknown> | null;
  }];
  /**
   * §30.5 hide. Optional `force` is the legacy hideRatingDialogBox(force) arg:
   * when force && hasChanges the chassis ignores the hide (the unsaved-guard).
   * Most callers (Cancel, post-Select cancelChanges) emit it with no arg. */
  'rating_mode:hide': [force?: boolean];
  /**
   * §30.2 unsaved-guard tracker. Emitted by the accessory MountingViewPopup
   * (parity §36 / §51.2 onChange) — the chassis treats it as a `makeChanges`
   * tick (marks hasChanges when #select is clickable). Static half of the
   * §30.4 family is the chassis button fan-out (dynamic, below). */
  'rating_mode:mounting:changed': [];
  /**
   * §30.5 chassis hide broadcast. hideRatingDialogBox triggers this; EVERY
   * live selector subscribes and treats it as cancel/cleanup (no re-trigger of
   * rating_mode:hide — that came from the chassis). */
  'cancel:dialog:changes': [];
  'rotate:canvas': [];
  /**
   * A catalogue field's filters moved (a params edit, or the Reset button).
   * Legacy has no channel: `updateFilter` and `deselectModel` both run
   * `resetCatalogue` (catalogue_select_field.js:1133-1147), which reaches into
   * the CatalogueSelector singleton it owns and re-renders the open list in
   * place. React's smart list is a component, so the engine announces the move
   * and CompTable decides — it reloads page 1 only when the list on screen is
   * the one this field opened.
   */
  'catalogue:filters-changed': [
    payload: {
      /** sender's `componentName` — the `rotate:canvas:<name>:table` channel. */
      name: string;
      /** owning form model id (the engine's `toggleSmartList` `component_id`). */
      component_id: number | null;
      /**
       * true — the filters have just moved: drop the rows and reload page 1.
       * Emitted SYNCHRONOUSLY with the filter write. Legacy runs its
       * `resetCatalogue` first in `updateFilter` too, but that awaits the new
       * count before clearing, so its old rows stay clickable for a round trip
       * and a click there undoes the edit; this is one round trip earlier on
       * purpose (see `announceFilterChange`).
       *
       * false — the refreshed count has landed: update the pagination bound and
       * nothing else. The two are deliberately independent, so a lost or
       * never-fetched count (`noFetchCount` configs) costs the bound, not the
       * rows.
       */
      reload: boolean;
      /**
       * false — this field never fetches a count (`noFetchCount` /
       * `skipFilteredCountOnInit`), so no `count` will ever arrive and page 1
       * is the whole list. The receiver bounds the list by page 1 for these,
       * which is what `toggleSmartList` does and what legacy's
       * `has_available_pages` does with `if (!self.cms_count) return false`.
       */
      counted: boolean;
      /**
       * The filtered `cms_count` — but ONLY when it is known to have been
       * counted against the filters that are live now. Null otherwise: on every
       * `reload: true` tick, for a field that does not count, and when the count
       * fetch failed or was overtaken (the engine keeps the previous filter's
       * number in `cmsCount`, so it cannot be read unconditionally).
       */
      count: number | null;
      /** the sender's `loader.load_page` half, reading the filters at call time. */
      loadPage: (page: number) => Promise<Record<string, unknown>[]>;
    },
  ];
  'close:manual-mode-active': [];
  'open_rating_popup': [];
  'show:duplicate:popup': [sectionId: number];
  // sectionId omitted = "use the current selection" (the legacy no-arg
  // direct call — §8.5 Delete-key path; context menus pass (null, id)).
  'show:delete:popup': [accessoryId: number | null, sectionId?: number];
  'certifications:save': [];
  'update:ahu:data': [data: Record<string, unknown>];
  'update:ahus': [];
  'workflow:done': [];
  'hide:show_allowable_filter_sizes': [];
}

/**
 * Dynamic-name event families (§25 "14 dynamic-name families"). Args are
 * untyped — each family's payload is documented in the §25 rows above.
 */
export type AhuBusDynamicEvent =
  | `change:casing:section_${number}`
  | `change:tunnel:widthOrHeight:${number}`
  | `changed:tunnel_${number}:sizes`
  | `changed:tunnel_${number}:width`
  | `changed:section:${number}:eadb_temp:eawb_temp`
  | `changed:secrion_${number}:casing_insulation_id` // typo load-bearing (§19.2)
  | `update:tunnel:${number}:fields`
  | `section:destroy:${number}`
  | `dmount:destroy:section:${number}`
  | `change:section:tag:${number}`
  | `refetch_fan_${number}_isp`
  | `force:render:fan:${number}`
  | `remove_accessory_esp_${number}`
  | `remove_component_esp_${number}`
  | `remove_damper_accessory_${number}`
  | `rotate:smart:list:${number}`
  | `ahu2d:section:${number}:accessories`
  | `rotate:canvas:${string}:table`
  | `choose:${string}:master`
  // ---- Part 6 dynamic families (§53.3 rule 5) ----
  | `render:blabk_off:${number}` // load-bearing typo (§49.3 / parity 307)
  | `deselct:model:${number}` // load-bearing typo (§49.3 / §49.4)
  | `select:model:for_${number}`
  | `section:change:qty:${number}`
  | `objParameters_${number}` // fan loadParams socket rebroadcast (§50.1)
  | `plate_heat_exchanger_${number}` // PHX dependency-cache rebroadcast (§50.1)
  | `damper_change_params_${number}`
  | `update:damper:${number}`
  | `update:filter:${number}` // filter form → filter selector re-init (§33.7)
  | `update_rating_dialog:${number}` // coil form → open coil selector hot-swap (§33.2)
  | `select:hood:${number}`
  | `rating_mode:hide:${number}` // selector → form ({selected}) (§49.2)
  /**
   * §30.4 chassis button → selector fan-out. `<activeRender>_<model_id>` keys
   * the live selector (model_id = current_model.id on board, the literal
   * 'header' in the Phase 6.5 independent-rating popup, which broadcasts on
   * `headerRatingBus` — component_selector.js:280). action ∈ print|reset|select|
   * cancel|show_notes|rate. The owning selector subscribes with the same key
   * in its mount effect; the chassis broadcasts on each control-button click. */
  | `${string}_${number}:selector:${'print' | 'reset' | 'select' | 'cancel' | 'show_notes' | 'rate'}`
  | `${string}_header:selector:${'print' | 'reset' | 'select' | 'cancel' | 'show_notes' | 'rate'}`
  | `${string}:model:change`
  | `storage:${string}:${'change' | 'destroy' | 'sync' | 'add' | 'remove' | 'create'}`;

export type AhuBusEventName = keyof AhuBusEvents | AhuBusDynamicEvent;

type AnyArgs = unknown[];
type Handler = (...args: AnyArgs) => void;

/**
 * Channels whose events are BUFFERED until a real consumer subscribes (the
 * int-wall pre-mount queue of parity 82/218 — replaces storage.js's 300 ms
 * canvas poll — plus the coil/evap/accessory caches replayed on mount).
 */
const BUFFERED_CHANNELS: ReadonlySet<string> = new Set([
  'draw:int_wall:on:canvas',
  'draw:coil:connection',
  'draw:evap_pack:connection',
  'draw:accessory:on:canvas',
]);

const BUFFER_LIMIT = 200;

interface SubscribeOptions {
  /**
   * false → passive subscription: does not count as a "real" consumer and
   * does not drain the replay buffer (used by the 6a no-op consumers so the
   * 6b canvas still receives buffered draws).
   */
  replayBuffered?: boolean;
}

class AhuBoardBus {
  private handlers = new Map<string, Set<Handler>>();

  /** Pending events for BUFFERED_CHANNELS with no real consumer yet. */
  private buffers = new Map<string, AnyArgs[]>();

  /** Channels that have at least one replay-consuming subscriber. */
  private consumed = new Set<string>();

  on<E extends keyof AhuBusEvents>(
    event: E,
    handler: (...args: AhuBusEvents[E]) => void,
    options?: SubscribeOptions
  ): () => void;
  on(event: AhuBusDynamicEvent, handler: Handler, options?: SubscribeOptions): () => void;
  on(event: string, handler: Handler, options: SubscribeOptions = {}): () => void {
    const { replayBuffered = true } = options;
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(handler);

    if (replayBuffered && BUFFERED_CHANNELS.has(event)) {
      this.consumed.add(event);
      const queue = this.buffers.get(event);
      if (queue) {
        this.buffers.delete(event);
        queue.forEach((args) => handler(...args));
      }
    }

    return () => {
      set?.delete(handler);
    };
  }

  emit<E extends keyof AhuBusEvents>(event: E, ...args: AhuBusEvents[E]): void;
  emit(event: AhuBusDynamicEvent, ...args: AnyArgs): void;
  emit(event: string, ...args: AnyArgs): void {
    if (BUFFERED_CHANNELS.has(event) && !this.consumed.has(event)) {
      const queue = this.buffers.get(event) ?? [];
      if (queue.length < BUFFER_LIMIT) queue.push(args);
      this.buffers.set(event, queue);
    }
    const set = this.handlers.get(event);
    if (!set) return;
    // Copy: a handler may unsubscribe itself mid-dispatch.
    [...set].forEach((h) => h(...args));
  }

  hasConsumer(event: AhuBusEventName): boolean {
    return (this.handlers.get(event)?.size ?? 0) > 0;
  }

  /** Full reset on board unmount (fresh-boot semantics, no cross-mount leaks). */
  reset(): void {
    this.handlers.clear();
    this.buffers.clear();
    this.consumed.clear();
  }
}

/** Singleton — one bus per board, like the legacy global dispatcher. */
export const ahuBoardBus = new AhuBoardBus();

/** The structural slice a selector tab needs in order to fan out (§30.4). Both
 * bus instances satisfy it; a selector accepts it as an optional `bus` prop
 * (selectorTypes.ts) and defaults to `ahuBoardBus`. */
export type SelectorBus = Pick<AhuBoardBus, 'on' | 'emit'>;

/**
 * Phase 6.5 — an ISOLATED bus for the header "Independent Rating" popup
 * (`#component_selector_button`).
 *
 * Legacy runs the header chassis on the SAME global `Dispatcher` as the board
 * chassis and relies on the `<render>_<model_id>` key namespace to keep them
 * apart: header mode always resolves `model_id` to the literal string
 * `'header'` (component_selector.js:243/249/261/267/274/280), so its keys can
 * never collide with a board key (`fan_13351:selector:rate`).
 *
 * The FAN selector however ALSO emits/consumes UNNAMESPACED channels
 * (`rating_mode:mounting:changed`, `rating_mode:hide`, `cancel:dialog:changes`,
 * `params_fan:change_combinations`, `minRequiredLengthChange`, …). On
 * `/ahu/detail/<id>` the header popup and a board rating dialog can be open at
 * the same time (discovery §7.3), so sharing one bus would let a keystroke in
 * the header popup arm the BOARD chassis's unsaved-changes guard — the React
 * analogue of the un-scoped `$('.control-buttons button[id="select"]')` bleed
 * legacy suffers from (discovery §7.3 trap 1). A second instance makes that
 * structurally impossible.
 *
 * NOTE: intentionally never `reset()` — the header popup is created once per
 * page load and never destroyed (header.js:525-528).
 */
export const headerRatingBus: SelectorBus = new AhuBoardBus();

/**
 * 6a placeholder: passive no-op consumers on every §25.2 canvas channel,
 * marking exactly where ViewCanvasTunnel/Canvas3D attach in 6b/6c. Passive
 * (replayBuffered:false) so they do NOT drain the draw-replay buffers the
 * real canvas needs at mount. Returns an unsubscribe-all cleanup.
 */
export function registerNoopCanvasConsumers(): () => void {
  const noop = () => undefined;
  const canvasChannels: Array<keyof AhuBusEvents> = [
    'auto:select:component',
    'show:on:canvas',
    'remove:selected:section',
    'validate:tunnel:after:section:destroy',
    'remove_accessory_from_canvas',
    'change:tunnel:dimensions',
    'sections:reordered',
    'draw:accessory:on:canvas',
    'deselect:section',
    'force:select:section',
    'canvas:zoom',
    'canvas:rotate',
    'hover:section',
    'canvas:set:view',
    'change:section:tag:canvas',
    'canvas:change:tunnel:index',
    'draw:int_wall:on:canvas',
    'update:int_wall:on:canvas',
    'remove:int_wall:from:canvas',
    'draw:coil:connection',
    'draw:evap_pack:connection',
    'accessoriesWithError',
    'sectionsWithError',
    'select:deselect:accessory',
    'hover:accessory',
    'redraw:canvas',
    'redraw:tunnel:lugs',
    'redraw:tunnel:lug',
    'select:tunnel:lugs',
    'highlight:tunnel:bases',
    'change_canvas_color_mode',
  ];
  const offs = canvasChannels.map((ch) =>
    ahuBoardBus.on(ch, noop, { replayBuffered: false })
  );
  return () => offs.forEach((off) => off());
}
